<template>
  <section class="stats-section" :style="teamColorVars">
    <template v-if="computedStats.length > 0">

      <!-- Rings: attacks, dangerous attacks, possession. -->
      <div v-if="rings.length" class="stats-rings">
        <div v-for="stat in rings" :key="'ring-' + stat.type" class="stats-ring">
          <div class="stats-ring__label">{{ stat.label }}</div>
          <div class="stats-ring__body">
            <span class="stats-value">{{ stat.h }}</span>
            <span class="stats-ring__chart-wrap">
              <svg class="stats-ring__chart" viewBox="0 0 40 40" aria-hidden="true">
                <circle class="stats-ring__track" cx="20" cy="20" r="16"/>
                <circle class="stats-ring__arc stats-ring__arc--away" cx="20" cy="20" r="16"
                        :stroke-dasharray="ringDash(stat.a_perc)" :stroke-dashoffset="-ringLength(stat.h_perc)"/>
                <circle class="stats-ring__arc stats-ring__arc--home" cx="20" cy="20" r="16"
                        :stroke-dasharray="ringDash(stat.h_perc)"/>
              </svg>
              <v-icon class="stats-ring__icon" size="14">{{ stat.icon }}</v-icon>
            </span>
            <span class="stats-value">{{ stat.a }}</span>
          </div>
        </div>
      </div>

      <!-- Corners and cards on the sides, shots in the middle. -->
      <div v-if="discipline.length || shots.length" class="stats-middle">
        <div class="stats-counters">
          <div v-for="c in discipline" :key="'h-' + c.type" class="stats-counter">
            <span class="stats-counter__icon" v-html="c.svg"></span>
            <span class="stats-counter__value">{{ c.h }}</span>
          </div>
        </div>

        <div v-if="shots.length" class="stats-shots">
          <div class="stats-shots__label">{{ $t('statistics.shotsvsontarget') }}</div>
          <div class="stats-shots__body">
            <span class="stats-value stats-value--sm">{{ shots.map(s => s.h).join('/') }}</span>
            <div class="stats-shots__bars">
              <div v-for="s in shots" :key="'shot-' + s.type" class="stats-bar">
                <div class="stats-bar__half stats-bar__half--home"><span :style="{ width: s.h_rel + '%' }"></span></div>
                <div class="stats-bar__half stats-bar__half--away"><span :style="{ width: s.a_rel + '%' }"></span></div>
              </div>
            </div>
            <span class="stats-value stats-value--sm">{{ shots.map(s => s.a).join('/') }}</span>
          </div>
        </div>

        <div class="stats-counters stats-counters--away">
          <div v-for="c in discipline" :key="'a-' + c.type" class="stats-counter">
            <span class="stats-counter__icon" v-html="c.svg"></span>
            <span class="stats-counter__value">{{ c.a }}</span>
          </div>
        </div>
      </div>

      <!-- Everything else: one row per stat. -->
      <div v-if="others.length" class="stats-list">
        <div v-for="stat in others" :key="'row-' + stat.type" class="stats-row">
          <div class="stats-row__info">
            <span class="stats-value stats-value--sm">{{ stat.h }}</span>
            <span class="stats-row__label">{{ stat.label }}</span>
            <span class="stats-value stats-value--sm">{{ stat.a }}</span>
          </div>
          <div class="stats-bar">
            <div class="stats-bar__half stats-bar__half--home"><span :style="{ width: stat.h_rel + '%' }"></span></div>
            <div class="stats-bar__half stats-bar__half--away"><span :style="{ width: stat.a_rel + '%' }"></span></div>
          </div>
        </div>
      </div>

    </template>
    <div v-else class="empty-state">
      {{ $t('statistics.nostats') }}
    </div>
  </section>
</template>

<script>
import {FULL_MAPPING} from "@/services/matchtracker/statNames";
import {resolveTeamColors} from "@/services/matchtracker/teamColors";

// Stats shown, with their icon; labels come from the statistics.* keys.
const STATS_MAP = {
  // Calcio
  1: 'mdi-flag-variant',
  2: 'mdi-target',
  3: 'mdi-arrow-top-right',
  4: 'mdi-chevron-right',
  5: 'mdi-chevron-triple-right',
  6: 'mdi-card',
  7: 'mdi-card',
  8: 'mdi-exclamation',
  9: 'mdi-soccer',
  11: 'mdi-percent-outline',
  12: 'mdi-whistle',
  13: 'mdi-shoe-cleat',
  14: 'mdi-soccer-field',
  15: 'mdi-flag-off',
  17: 'mdi-hand-ball',
  2046: 'mdi-soccer',

  // Tennis / Tennistavolo
  20: 'mdi-tennis-ball',
  21: 'mdi-alert-circle-outline',
  22: 'mdi-tennis',
  23: 'mdi-target-variant',
  34: 'mdi-trophy-variant-outline',
  66: 'mdi-check',
  67: 'mdi-shield-check',
  2008: 'mdi-tennis',
  2194: 'mdi-percent',
  2195: 'mdi-tennis',
  2196: 'mdi-tennis',

  // Cricket
  93: 'mdi-cricket',
  154: 'mdi-clock-outline',
  155: 'mdi-cricket',
  156: 'mdi-numeric-6-box-outline',
  160: 'mdi-plus',
  161: 'mdi-cancel',
  164: 'mdi-baseball',
  166: 'mdi-chart-line',
  170: 'mdi-numeric-4-box-outline',
  1078: 'mdi-account-multiple'
};

const RING_TYPES = [4, 5, 11];
// Order on the home side (mirrored on the away side): corners, red, yellow.
const DISCIPLINE_TYPES = [1, 7, 6];
// Total shots on top, shots on target below.
const SHOT_TYPES = [2046, 2];
// Goals are already in the score above.
const HIDDEN_TYPES = [9];

const RING_LENGTH = 2 * Math.PI * 16;
const RING_GAP = 1.5;

const DISCIPLINE_SVGS = {
  1: '<svg viewBox="0 0 24 24"><path d="M6 3v18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M6.9 3.6c3-1.3 5.2 1.4 10.8.2l-2.6 4 2.6 4c-5.6 1.2-7.8-1.5-10.8-.2z" fill="#ef4444"/></svg>',
  6: '<svg viewBox="0 0 24 24"><rect x="6.5" y="3" width="11" height="16" rx="2" transform="rotate(10 12 11)" fill="#facc15"/></svg>',
  7: '<svg viewBox="0 0 24 24"><rect x="6.5" y="3" width="11" height="16" rx="2" transform="rotate(10 12 11)" fill="#ef4444"/></svg>'
};

export default {
  name: 'StatsSection',
  props: {
    value: Boolean,
    event: Object
  },
  methods: {
    // Arc of `perc`% of the ring, minus a small gap between the two teams.
    ringLength(perc) {
      return RING_LENGTH * perc / 100;
    },
    ringDash(perc) {
      const len = Math.max(0, this.ringLength(perc) - (perc > 0 && perc < 100 ? RING_GAP : 0));
      return `${len} ${RING_LENGTH}`;
    }
  },
  computed: {
    // Charts in the teams' shirt colours, the same the MatchTracker uses.
    teamColorVars() {
      const { home, away } = resolveTeamColors(this.event);
      return { '--stats-home': home, '--stats-away': away };
    },
    lsData() {
      if (this.event && this.event.livescore && this.event.livescore.scoreboard) {
        return this.event.livescore;
      }
      if (this.event && this.event.fixtureLiveScore && this.event.fixtureLiveScore.length > 0 && this.event.fixtureLiveScore[0].scoreboard) {
        return this.event.fixtureLiveScore[0];
      }
      return null;
    },
    isValidData() {
      return this.lsData !== null && this.lsData.scoreboard !== undefined;
    },
    computedStats() {
      if (!this.isValidData || !this.lsData.statistics) return [];
      return this.lsData.statistics
          .map(s => {
            const typeId = parseInt(s.type);
            const icon = STATS_MAP[typeId];
            if (!icon || HIDDEN_TYPES.includes(typeId)) return null;

            const r1 = s.results ? s.results.find(r => String(r.position) === "1") : null;
            const r2 = s.results ? s.results.find(r => String(r.position) === "2") : null;

            const h = parseFloat(r1 ? r1.value : 0) || 0;
            const a = parseFloat(r2 ? r2.value : 0) || 0;
            const total = h + a;
            const max = Math.max(h, a);

            return {
              type: typeId,
              label: this.$t(FULL_MAPPING[typeId] || 'statistics.event'),
              icon,
              h, a,
              h_perc: total > 0 ? (h / total) * 100 : 50,
              a_perc: total > 0 ? (a / total) * 100 : 50,
              // Bars grow from the centre: the leading team fills its half.
              h_rel: max > 0 ? (h / max) * 100 : 0,
              a_rel: max > 0 ? (a / max) * 100 : 0
            };
          })
          .filter(s => s !== null)
          .sort((a, b) => a.type - b.type);
    },
    rings() {
      return RING_TYPES.map(t => this.computedStats.find(s => s.type === t)).filter(Boolean);
    },
    discipline() {
      return DISCIPLINE_TYPES
          .map(t => this.computedStats.find(s => s.type === t))
          .filter(Boolean)
          .map(s => ({ ...s, svg: DISCIPLINE_SVGS[s.type] }));
    },
    shots() {
      return SHOT_TYPES.map(t => this.computedStats.find(s => s.type === t)).filter(Boolean);
    },
    others() {
      const shown = [...RING_TYPES, ...DISCIPLINE_TYPES, ...SHOT_TYPES];
      return this.computedStats.filter(s => !shown.includes(s.type));
    }
  }
}
</script>

<style scoped>
/* --stats-home / --stats-away: shirt colours, set inline. */
.stats-section {
  color: var(--text-platform);
  padding: 8px 4px 12px;
}

.stats-value {
  font-size: var(--font-size-5xl);
  font-weight: 700;
  min-width: 22px;
  text-align: center;
}

.stats-value--sm {
  font-size: var(--font-size-2xl);
}

/* Rings */
.stats-rings {
  display: flex;
  justify-content: space-around;
  gap: 8px;
  padding-bottom: 16px;
}

.stats-ring {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}

.stats-ring__label {
  font-size: var(--font-size-2xl);
  opacity: 0.75;
  margin-bottom: 6px;
  text-align: center;
  white-space: nowrap;
}

.stats-ring__body {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stats-ring__chart-wrap {
  position: relative;
  display: block;
  width: 40px;
  height: 40px;
}

.stats-ring__chart {
  display: block;
  width: 40px;
  height: 40px;
  /* Start at the top and run anticlockwise: home arc on the left, away on the right. */
  transform: scaleX(-1) rotate(-90deg);
}

.stats-ring__track {
  fill: none;
  stroke: rgba(255, 255, 255, 0.08);
  stroke-width: 3.5;
}

.stats-ring__arc {
  fill: none;
  stroke-width: 3.5;
  transition: stroke-dasharray 0.4s ease, stroke-dashoffset 0.4s ease;
}

.stats-ring__arc--home { stroke: var(--stats-home); }
.stats-ring__arc--away { stroke: var(--stats-away); }

.stats-ring__icon {
  position: absolute !important;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: var(--text-platform) !important;
  opacity: 0.85;
}

/* Corners / cards and shots */
.stats-middle {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: end;
  gap: 12px;
  padding: 12px 12px 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.stats-counters {
  display: flex;
  gap: 10px;
}

.stats-counters--away {
  flex-direction: row-reverse;
}

.stats-counter {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stats-counter__icon,
.stats-counter__icon ::v-deep svg {
  display: block;
  width: 14px;
  height: 14px;
}

.stats-counter__value {
  font-size: var(--font-size-3xl);
  font-weight: 700;
}

.stats-shots {
  min-width: 0;
}

.stats-shots__label {
  font-size: var(--font-size-xl);
  opacity: 0.75;
  text-align: center;
  margin-bottom: 4px;
  white-space: nowrap;
}

.stats-shots__body {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stats-shots__bars {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Mirrored bar: home grows left from the centre, away grows right. */
.stats-bar {
  display: flex;
  gap: 2px;
  height: 4px;
}

.stats-bar__half {
  flex: 1;
  display: flex;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
}

.stats-bar__half--home { justify-content: flex-end; }

.stats-bar__half span {
  display: block;
  height: 100%;
  border-radius: 2px;
  transition: width 0.4s ease;
}

.stats-bar__half--home span { background: var(--stats-home); }
.stats-bar__half--away span { background: var(--stats-away); }

/* Other stats */
.stats-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 12px 0;
  margin-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.stats-row__info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.stats-row__label {
  font-size: var(--font-size-2xl);
  opacity: 0.75;
  text-align: center;
}

.empty-state { display: flex; flex-direction: column; align-items: center; padding: 40px 0; font-size: var(--font-size-2xl); color: var(--text-op-50-platform) }
</style>
